import { CSSProperties, ReactNode, useMemo } from 'react'

import { RemoveCircleOutline } from '@mui/icons-material'
import { FormHelperText, IconButton, Paper, Stack, Tooltip } from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { parse } from 'date-fns/esm'

import { FieldHeader } from './FieldHeader'
import { TimeWindows } from './TimeWindows'

import { useDateFnsLocale } from '@/hooks'
import { useActions } from './../hooks/useActions'

import { useTexts } from '../hooks/useTexts'

import type { TimeWindowExceptionValidation } from '../../../../../../../typings'

const MainPaperStyle: CSSProperties = {
  background: '#F3F5F7',
  padding: '16px 8px',
}

const SecondaryPaperStyle: CSSProperties = {
  background: '#F9FAFB',
  padding: '16px 8px',
  marginTop: 2,
}

type Props = {
  label: string
  testId?: string
  value: Record<string, uui.domain.rm.TimeWindow[]>
  disabled: boolean
  helperText: ReactNode
  validate: (force?: boolean) => void
  onChange: (value: Record<string, uui.domain.rm.TimeWindow[]>) => void
}

type TimeWindowFieldValueCouple = [uui.domain.rm.TimeWindow?, uui.domain.rm.TimeWindow?]

export function TimeWindowException(props: Props) {
  const {
    label,
    testId = 'timeWindowsExceptions',
    value,
    disabled,
    helperText,
    validate,
    onChange,
  } = props

  const texts = useTexts()

  const locale = useDateFnsLocale()

  const actions = useActions(value, onChange)

  const addTimeWindowsExceptionDisabled = Object.keys(value).length === 2

  const now = new Date()

  // Parse helperText into a Map for O(1) lookup by date
  const errors = useMemo<Record<string, string>>(() => {
    if (typeof helperText === 'string') {
      try {
        const parsed = JSON.parse(helperText) as Record<string, TimeWindowExceptionValidation>
        const result: Record<string, string> = {}
        for (const [date, validation] of Object.entries(parsed)) {
          const error = validation.date || validation.timeWindows?.find(tw => tw !== '')
          if (error) {
            result[date] = error
          }
        }
        return result
      } catch {
        return {}
      }
    }
    return {}
  }, [helperText])

  return (
    <Stack data-testid={testId} data-trackid={testId} spacing={1}>
      <FieldHeader
        label={label}
        action={{
          label: texts.addTimeWindowExceptionLabel,
          tooltip: texts.addTimeWindowExceptionTooltip,
          disabledTooltip: texts.addTimeWindowExceptionDisabledTooltip,
          onClick: actions.onAddException,
          disabled: addTimeWindowsExceptionDisabled || disabled,
        }}
        testId={`${testId}-section-header`}
      />

      {Object.entries(value).map(([date, timeWindows], index) => (
        <Paper elevation={0} sx={MainPaperStyle}>
          <Stack direction="row" spacing={1} alignItems="center">
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
              <DatePicker
                value={parse(date, 'yyyyMMdd', now)}
                onChange={value => actions.onUpdateException(index, value)}
                label={texts.timeWindowExceptionDateLabel}
                slotProps={{ textField: { required: true } }}
              />
            </LocalizationProvider>

            <Tooltip title={texts.timeWindowExceptionRemoveTooltip}>
              <IconButton
                color="error"
                onClick={() => actions.onRemoveException(index)}
                data-testId={`${testId}-remove-button`}
                data-trackId={`${testId}-remove-button`}
              >
                <RemoveCircleOutline />
              </IconButton>
            </Tooltip>
          </Stack>

          <Paper elevation={0} sx={SecondaryPaperStyle}>
            <TimeWindows
              label={texts.timeWindowsLabel}
              testId={`${testId}-time-windows`}
              value={timeWindows as TimeWindowFieldValueCouple}
              disabled={disabled}
              validate={validate}
              onChange={value => actions.onUpdateTimeWindows(value, index)}
            />
          </Paper>

          {errors[date] && <FormHelperText error>{errors[date]}</FormHelperText>}
        </Paper>
      ))}
    </Stack>
  )
}
